import React from "react";
import {
	calculateShortIsolation,
	calculateSponningDimensions,
	getBackgroundColor,
	shouldShowPart
} from "../utils.js";

/**
 * Part component that renders individual 3D parts of a panel construction
 * Handles complex visibility logic, sponning (edge finishing) calculations, and 3D positioning
 * Each part represents a layer (outer, inner, core, insulation) with specific materials and dimensions
 *
 * @param {Object} props - Component props (using camelCase naming)
 * @param {string} props.partName - Name/type of the part (buitenlaag, binnenlaag, kernplaat, isolatie, etc.)
 * @param {Object} props.product - Product configuration data
 * @param {Object} props.properties - Form properties and settings
 * @param {number} props.depth - Z-axis depth/thickness of the part in pixels
 * @param {number} props.material - Material ID for the part
 * @param {Object} props.article - Article/product data for the part
 * @param {boolean} props.isolation - Whether this part is an insulation layer
 * @param {boolean} props.isolation1 - Whether this is the first insulation layer
 * @param {boolean} props.isolation2 - Whether this is the second insulation layer
 * @param {boolean} props.singleIsolation - Whether using single insulation configuration
 * @param {boolean} props.frontWhenSingleIsolation - Front position for single insulation
 * @param {boolean} props.frontWhenBI - Front position for inner layer (BI)
 * @param {boolean} props.insideView - Whether showing inside view of the panel
 * @param {boolean} props.onlyKernplaat - Whether showing only core plate
 * @param {number} props.onlyMaterial - Material filter for selective display
 * @param {Object} props.onlyArticle - Article filter for selective display
 * @param {string} props.sponning - Sponning/edge finishing type (SP_BU or SP_BI)
 * @param {boolean} props.colors - Whether to apply color styling
 * @param {string} props.color - Color value for the part (hex or color name)
 */
const Part = ({
    partname: partName,
    product,
    properties,
    depth,
    material,
    article,
    isolation,
    isolation1,
    isolation2,
    singleisolation: singleIsolation,
    frontWhenSingleIsolation,
    frontwhenBI: frontWhenBI,
    insideview: insideView,
    onlykernplaat: onlyKernplaat,
    onlymaterial: onlyMaterial,
    onlyarticle: onlyArticle,
    sponning,
    colors,
    color,
}) => {
    // Determine if this part should be shown using utility function
    const shouldShow = shouldShowPart({
        isolation,
        onlyKernplaat,
        onlyMaterial,
        onlyArticle,
        article,
        material
    });

    // Early return if not visible
    if (!shouldShow) {
        return <></>;
    }

    // Calculate sponning (edge finishing) dimensions and positioning using utility
    const sponningCalc = calculateSponningDimensions(
        properties,
        product,
        sponning,
        insideView,
        singleIsolation,
        frontWhenSingleIsolation,
        isolation,
        isolation1,
        isolation2,
        partName,
    );

    // Determine background color using utility
    const backgroundColor = getBackgroundColor(colors, color);

    // Calculate special styling for shortened isolation layers using utility
    const shortIsolation = calculateShortIsolation({
        isolation1,
        isolation2,
        sponning,
        properties
    });

    return (
        <div className={`cube ${partName} ${shortIsolation}`}>
            {/* Dynamic CSS styling for 3D positioning and dimensions */}
            <style jsx>
                {`
                    .cube {
                        width: ${depth}px;
                        height: ${sponningCalc.blockHeight}px !important;
                        transform: rotateZ(0deg)
                            translateZ(${sponningCalc.marginLeft}px) !important;
                        margin-top: ${sponningCalc.marginTop}px !important;
                    }

                    .cube::before {
                        width: ${sponningCalc.blockWidth}px !important;
                        height: ${sponningCalc.blockHeight}px !important;
                        transform: rotateY(90deg) translateX(0)
                            translateZ(${!insideView ? depth : 0}px) !important;
                    }

                    .cube.buitenlaag::before,
                    .cube.binnenlaag::before {
                        background-color: ${backgroundColor} !important;
                    }

                    .cube::after {
                        width: ${depth}px;
                        height: ${sponningCalc.blockWidth}px !important;
                    }
                `}
            </style>
        </div>
    );
};



export default Part;
